import type { ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/cn';
import { shortId } from '@/lib/format';

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'accent';

const TONE: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-muted',
  ok:      'bg-ok-soft text-ok',
  warn:    'bg-warn-soft text-warn',
  danger:  'bg-danger-soft text-danger',
  accent:  'bg-accent-soft text-accent-ink',
};

export function Badge({ tone = 'neutral', mono, dot, className, title, children }: {
  tone?: Tone;
  mono?: boolean;
  /** A leading dot in the tone colour — status at a glance. */
  dot?: boolean;
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4 whitespace-nowrap',
        mono && 'font-mono font-medium tracking-tight',
        TONE[tone], className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/** A UUID shown short, copied whole on click. */
export function IdChip({ id, className }: { id: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={`${id} — нажмите, чтобы скопировать`}
      className={cn(
        'inline-flex items-center gap-1 rounded-md bg-surface-2 px-1.5 py-0.5 font-mono text-[11px] text-muted',
        'transition-colors hover:bg-surface-3 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
        className,
      )}
    >
      {shortId(id)}
      {copied ? <Check size={11} className="text-ok" /> : <Copy size={11} className="opacity-60" />}
    </button>
  );
}
